import {
    formatDate,
    formatGender,
    formatNameFirstLast,
    formatPhoneNumber,
    getAge,
} from '@strata/helper-functions';
import { EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { MailToHyperlink } from '@strata/react-hyperlink';
import { AddressDetailLabelValue } from '@strata/react-label-value-pairs';

import { EnrollmentStatusIndicator } from 'components/enrollment/EnrollmentStatusIndicator';
import {
    ENROLLMENT_HISTORY_ROWS,
    formatAsoDisplay,
    formatEnrollmentHistoryColumnTitle,
    formatEnrollmentSourceDisplay,
    formatMarketplaceDisplay,
    formatRelationshipCodeDisplay,
    hasSnapshotRowChanged,
} from 'services/enrollmentHistoryHelpers';

const getRowCellContent = (snapshot, rowKey) => {
    switch (rowKey) {
        case 'enrollmentStatus':
            return snapshot.enrollmentStatus ? (
                <span className="sds_value sds_value--detail">
                    <EnrollmentStatusIndicator status={snapshot.enrollmentStatus} />
                </span>
            ) : undefined;
        case 'coverageDates':
            return (
                <span className="sds_value sds_value--detail">
                    <EnrollmentCoverageDates
                        enrollmentStart={snapshot.enrollmentStart}
                        enrollmentEnd={snapshot.enrollmentEnd}
                        isFmhEnrollmentEnd={snapshot.isFmhEnrollmentEnd}
                    />
                </span>
            );
        case 'enrollmentSource':
            return formatEnrollmentSourceDisplay(snapshot.enrollmentSource);
        case 'name':
            return formatNameFirstLast(snapshot.firstName, snapshot.lastName);
        case 'gender':
            return formatGender(snapshot.gender);
        case 'dateOfBirth':
            return formatDate(snapshot.dateOfBirth);
        case 'age':
            return getAge(snapshot.dateOfBirth);
        case 'address':
            return (
                <AddressDetailLabelValue
                    label=""
                    address1={snapshot?.memberAddress1}
                    address2={snapshot?.memberAddress2}
                    city={snapshot?.memberCity}
                    state={snapshot?.memberState}
                    zip={snapshot?.memberZip}
                    className="sds_enrollment-comparison__data-cell"
                />
            );
        case 'phone':
            return formatPhoneNumber(snapshot.phone);
        case 'email':
            return snapshot.email ? <MailToHyperlink emailAddress={snapshot.email} /> : undefined;
        case 'relationshipCode':
            return formatRelationshipCodeDisplay(snapshot.relationshipCode);
        case 'isMarketplaceEligible':
            return formatMarketplaceDisplay(snapshot.isMarketplaceEligible);
        case 'isAso':
            return formatAsoDisplay(snapshot.isAso);
        default:
            return snapshot[rowKey];
    }
};

const buildCellValue = (snapshot, rowKey) => {
    const cellContent = getRowCellContent(snapshot, rowKey);

    if (hasSnapshotRowChanged(snapshot, rowKey)) {
        return { value: cellContent, updated: true };
    }

    return cellContent;
};

export const buildEnrollmentHistoryColumns = (histories) => {
    if (!histories?.length) {
        return { rows: [], columns: [] };
    }

    const rows = ENROLLMENT_HISTORY_ROWS.map((row) => ({
        key: row.key,
        label: row.label,
    }));

    const columns = histories.map((history) => ({
        columnTitle: formatEnrollmentHistoryColumnTitle(history.changedAt),
        values: Object.fromEntries(rows.map((row) => [row.key, buildCellValue(history, row.key)])),
    }));

    return { rows, columns };
};
